import React, { memo, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import getTooltipData from './tooltipData';
import { scoreToColor } from './areaData.js';
import { buildListingHoverCard, fillListingHoverCommute } from './listingHoverCard.js';
import { useCommute } from './commute.js';
import CompareCard from './CompareCard.js';
import { useComparedAreas } from './compareAreas.js';

// Listvy's info panel under the desktop map: kartvy's area card for the hovered area, or the previewed
// listing (hovered or selected card or map marker). It owns the hover state, so moving over the map
// re-renders only this panel, not the whole page (the listing cards and the filter column).
// The page reports hovers through `handleRef`: setHoveredArea(code, name), clearHoveredArea(code),
// setHoveredMapListing(id), clearHoveredMapListing(id).
// Both the last hovered area and the last previewed listing stay until something else is hovered.
// While areas are compared (a double click on the map, compareAreas.js) the panel holds the comparison card
// instead (design A of the "Jämföra områden" canvas): two areas show side by side, the rest scroll.

const METRIC_LABELS = [
    ['apartment-sqm', 'Lägenhet kr/m²', 'Apt SEK/m²'],
    ['house-sqm', 'Villa kr/m²', 'House SEK/m²'],
    ['income', 'Netto/mån', 'Net/month'],
    ['unemployment', 'Arbetslöshet', 'Unemployment'],
    ['owned', 'Äganderätt', 'Owned homes'],
    ['education', 'Utbildade', 'Educated'],
];

const ListViewInfoPanel = ({
    handleRef,
    languageState,
    areaStats,
    colorCache,
    regsoNameById,
    listingById,
    hoveredListingId,
    mapSelectedListingId,
    helpers,
    // (regsoCode) => whether the filters leave the area out; stable (reads the page's latest through a ref).
    isAreaFilteredOut,
    // (codes) => open the comparison's page over the view.
    onComparePreview,
}) => {
    const swedish = Boolean(languageState?.swedish);
    const compared = useComparedAreas();
    // Tablets (one column) have a wide panel: more areas fit side by side.
    const [tablet, setTablet] = useState(() => typeof window !== 'undefined' && Boolean(window.matchMedia?.('(max-width: 1200px)').matches));
    useEffect(() => {
        const query = window.matchMedia?.('(max-width: 1200px)');
        if (!query) return undefined;
        const onChange = () => setTablet(query.matches);
        query.addEventListener?.('change', onChange);
        return () => query.removeEventListener?.('change', onChange);
    }, []);
    const [hoveredArea, setHoveredArea] = useState(null); // { code, name }
    const [mapHoveredListingId, setMapHoveredListingId] = useState(null);

    useImperativeHandle(handleRef, () => ({
        setHoveredArea: (code, name) => setHoveredArea(code ? { code, name } : null),
        clearHoveredArea: (code) => setHoveredArea((current) => (!code || current?.code === code ? null : current)),
        setHoveredMapListing: (id) => setMapHoveredListingId(id || null),
        clearHoveredMapListing: (id) => setMapHoveredListingId((current) => (!id || current === id ? null : current)),
    }), []);

    const hoveredAreaCode = hoveredArea?.code || '';
    const livePreviewListingId = mapHoveredListingId || hoveredListingId || mapSelectedListingId || null;
    const [lastPreviewListingId, setLastPreviewListingId] = useState(null);
    useEffect(() => {
        if (livePreviewListingId) setLastPreviewListingId(livePreviewListingId);
    }, [livePreviewListingId]);
    useEffect(() => {
        if (hoveredAreaCode) setLastPreviewListingId(null);
    }, [hoveredAreaCode]);
    const previewListingId = livePreviewListingId || lastPreviewListingId;
    const previewListing = useMemo(
        () => (previewListingId ? listingById.get(String(previewListingId)) || null : null),
        [listingById, previewListingId],
    );
    const previewRegsoCode = previewListing ? helpers.extractListingRegsoCode(previewListing) : '';

    const [lastHoveredArea, setLastHoveredArea] = useState(null);
    useEffect(() => {
        if (hoveredArea) setLastHoveredArea(hoveredArea);
    }, [hoveredArea]);
    const activeRegsoCode = hoveredAreaCode || previewRegsoCode || lastHoveredArea?.code || '';
    let activeRegsoName = '';
    if (activeRegsoCode) {
        if (hoveredArea?.name) activeRegsoName = hoveredArea.name;
        else if (activeRegsoCode === lastHoveredArea?.code && lastHoveredArea.name) activeRegsoName = lastHoveredArea.name;
        else activeRegsoName = regsoNameById.get(activeRegsoCode) || activeRegsoCode;
    }

    // A new listing preview switches the panel to the listing; hovering another area switches it back.
    const [tab, setTab] = useState('area');
    useEffect(() => {
        if (previewListingId) setTab('listing');
    }, [previewListingId]);
    useEffect(() => {
        if (hoveredAreaCode) setTab('area');
    }, [hoveredAreaCode]);
    const view = previewListing && tab === 'listing' ? 'listing' : 'area';

    // The previewed listing: kartvy's hover card for one listing (listingHoverCard.js).
    const listingCardHtml = useMemo(() => {
        if (view !== 'listing' || !previewListing) return '';
        const score = helpers.normalizeListingScore(
            previewListing.betyg ?? (previewRegsoCode ? areaStats[`combined_${previewRegsoCode}`] : null),
        );
        return buildListingHoverCard(previewListing, { score, swedish });
    }, [areaStats, helpers, previewListing, previewRegsoCode, swedish, view]);

    // The card's travel-time chip, filled once the time is known (and again for a new workplace or mode).
    const listingCardRef = useRef(null);
    const commute = useCommute();
    useEffect(() => {
        if (listingCardHtml) fillListingHoverCommute(listingCardRef.current, previewListing, swedish);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [listingCardHtml, commute]);

    const areaCardHtml = useMemo(
        () => (view === 'area' && activeRegsoCode
            ? getTooltipData(activeRegsoName, activeRegsoCode, colorCache, areaStats, scoreToColor, languageState, {
                filteredOut: Boolean(isAreaFilteredOut?.(activeRegsoCode)),
            })
            : ''),
        [activeRegsoCode, activeRegsoName, areaStats, colorCache, isAreaFilteredOut, languageState, view],
    );

    if (compared.length) {
        return (
            <div className="list-view-map-panels">
                <CompareCard
                    swedish={swedish}
                    colorCache={colorCache}
                    areaStats={areaStats}
                    variant="panel"
                    maxVisible={tablet ? 4 : 2}
                    showGhost
                    view="listvy"
                    onPreview={onComparePreview}
                    className="list-view-compare-card"
                />
            </div>
        );
    }

    return (
        <div className="list-view-map-panels">
            <section
                className={`list-view-map-panel list-view-info${activeRegsoCode || view === 'listing' ? '' : ' is-empty'}`}
                aria-label={view === 'listing'
                    ? (swedish ? 'Vald bostad' : 'Selected listing')
                    : (swedish ? 'Områdesstatistik' : 'Area stats')}
            >
                {view === 'listing' ? (
                    <div
                        ref={listingCardRef}
                        className="list-view-info-listingcard area-card"
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{ __html: listingCardHtml }}
                    />
                ) : activeRegsoCode ? (
                    <div
                        className="list-view-info-areacard"
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{ __html: areaCardHtml }}
                    />
                ) : (
                    <>
                        <header className="list-view-info-header">
                            <div className="list-view-info-heading">
                                <h3>{swedish ? 'Områdesstatistik' : 'Area stats'}</h3>
                                <p>{swedish ? 'Hovra över ett område på kartan' : 'Hover an area on the map'}</p>
                            </div>
                            <span className="list-view-info-total-badge is-placeholder" aria-hidden="true">
                                <svg viewBox="0 0 24 24">
                                    <path d="M12 21s-7-6.2-7-11.5A7 7 0 0 1 19 9.5C19 14.8 12 21 12 21z" />
                                    <circle cx="12" cy="9.5" r="2.5" />
                                </svg>
                            </span>
                        </header>
                        <dl className="list-view-info-metrics">
                            {METRIC_LABELS.map(([id, sv, en]) => (
                                <div key={id} className="list-view-info-metric">
                                    <div className="list-view-info-metric-top">
                                        <dt>{swedish ? sv : en}</dt>
                                        <dd className="is-skeleton" aria-hidden="true" />
                                    </div>
                                </div>
                            ))}
                        </dl>
                    </>
                )}
            </section>
        </div>
    );
};

export default memo(ListViewInfoPanel);
